import { useCountByStatus } from "@/api/audiovisual/audiovisual";
import { PagingParameters } from "@/api/dtos";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { Audiovisual } from "@/lib/types";
import { ColumnDef } from "@tanstack/react-table";
import { ChevronLeft, PlusCircle, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const AudiovisualLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedAudiovisuals, setPaginationRelatedAudiovisuals] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedAudiovisual",
    getPaginationRelatedAudiovisuals.pageSize,
    getPaginationRelatedAudiovisuals.page *
      getPaginationRelatedAudiovisuals.pageSize,
  );

  const { data: countRelatedAudiovisuals } = useCountRelatedFields(id, type, [
    "relatedAudiovisual",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedAudiovisual",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedAudiovisual",
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const [searchContent, setSearchContent] = useState<string>("");

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const { data: notRelatedAudiovisual } = useGetNotRelatedResource<Audiovisual>(
    {
      resourceId: id,
      resultType: "audiovisual",
      searchQuery: searchContent,
      pagination: getPagination,
    },
  );

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteRelation(id);
    },
    [deleteRelation],
  );

  const columns: ColumnDef<Audiovisual, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "audiovisuelDescriptionNumeroEnregistrement",
        header: t("audiovisuelDescriptionNumeroEnregistrement"),
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.audiovisuelDescriptionNumeroEnregistrement}
            />
          );
        },
      },
      {
        accessorKey: "audiovisuelDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"audiovisual"}
            />
          );
        },
      },
      {
        accessorKey: "audiovisuelDescriptionTitre",
        header: t("audiovisuelDescriptionTitre"),
        size: 400,
        cell: ({ row }) => {
          return <HtmlCell value={row.original.audiovisuelDescriptionTitre} />;
        },
      },
      {
        accessorKey: "audiovisuelDescriptionDateDeCreation",
        header: t("audiovisuelDescriptionDateDeCreation"),
        size: 100,
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.audiovisuelDescriptionDateDeCreation}
            />
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          if (isAddMode) {
            return (
              <div className="flex items-center gap-1 justify-end mr-2">
                <ShowElements
                  type="audiovisual"
                  id={row.original.id}
                  elements={[
                    {
                      label: t("audiovisuelDescriptionNumeroEnregistrement"),
                      value:
                        row.original.audiovisuelDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("audiovisuelDescriptionResume"),
                      value: row.original.audiovisuelDescriptionResume,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.attachments,
                      type: "image",
                    },
                  ]}
                  onAdd={() => handleCreateRelation(row.original.id)}
                />
                <Button
                  className="font-semibold flex gap-2 p-1"
                  variant="ghost"
                  onClick={() => handleCreateRelation(row.original.id)}
                  type="button"
                >
                  <PlusCircle className="w-5 h-5 cursor-pointer text-[#005D8D]" />
                </Button>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(row.original.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ShowElements
                type="audiovisual"
                id={row.original.id}
                elements={[
                  {
                    label: t("audiovisuelDescriptionNumeroEnregistrement"),
                    value:
                      row.original.audiovisuelDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("audiovisuelDescriptionResume"),
                    value: row.original.audiovisuelDescriptionResume,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          );
        },
      },
    ];
  }, [handleCreateRelation, handleDeleteRelation, isAddMode, t]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between mb-2">
        <h2 className="text-lg font-semibold">{t("linkedAudiovisuals")}</h2>
        {!isAddMode && (
          <Button variant="outline" onClick={handleAddMode}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      <DataTable
        columns={columns}
        data={
          isAddMode
            ? (notRelatedAudiovisual?.data ?? [])
            : (relatedFields ?? [])
        }
        pagination={
          !isAddMode
            ? {
                pagination: getPaginationRelatedAudiovisuals,
                setPage: setPaginationRelatedAudiovisuals,
                totalSize: countRelatedAudiovisuals?.relatedAudiovisual,
              }
            : {
                pagination: getPagination,
                setPage: setPagination,
                totalSize: totalSize,
              }
        }
        options={{
          enableColumnResizing: true,
        }}
      />
    </div>
  );
};

export default AudiovisualLinks;
